/**
 * Model-aware adapter factory — resolves model_class to cached SdkAdapter instances.
 *
 * Used by executeWorkflow (per-step routing) and shares cache across steps.
 * The factory's no-arg / standard call must return synchronously (workflow-runner
 * resolveAdapter contract); other model classes may return a Promise on first use.
 */

import { createAdapter, type CreateAdapterOptions } from "./adapter-factory.js";
import {
  createModelResolver,
  type ModelClass,
  type ModelMappingConfig,
  type ModelResolver,
  type ResolvedModel,
} from "./model-resolver.js";
import type { ModelAwareSdkAdapterFactory } from "./workflow-runner.js";
import type { SdkAdapter } from "./task-runner.js";

export interface ModelAwareAdapterSetup {
  factory: ModelAwareSdkAdapterFactory;
  defaultAdapter: SdkAdapter;
  modelResolver: ModelResolver;
  /** Resolve (and cache) the adapter for a model_class. */
  resolveAdapter: (modelClass?: ModelClass) => Promise<SdkAdapter>;
}

function cacheKey(resolved: ResolvedModel): string {
  return `${resolved.adapter}:${resolved.model ?? ""}`;
}

export async function createModelAwareAdapterSetup(options: {
  fallbackAdapter: string;
  fallbackModel?: string;
  modelMapping?: ModelMappingConfig;
  adapterOptions?: Partial<CreateAdapterOptions>;
  pinAdapter?: boolean;
}): Promise<ModelAwareAdapterSetup> {
  const modelResolver = createModelResolver({
    modelMapping: options.modelMapping,
    fallbackAdapter: options.fallbackAdapter,
    fallbackModel: options.fallbackModel,
    pinnedAdapter: options.pinAdapter ? options.fallbackAdapter : undefined,
  });

  const cache = new Map<string, SdkAdapter>();
  const inflight = new Map<string, Promise<SdkAdapter>>();

  async function ensureAdapter(modelClass?: ModelClass): Promise<SdkAdapter> {
    const resolved = modelResolver.resolve(modelClass);
    const key = cacheKey(resolved);
    const hit = cache.get(key);
    if (hit) return hit;

    let pending = inflight.get(key);
    if (!pending) {
      pending = createAdapter(resolved.adapter, {
        model: resolved.model,
        ...options.adapterOptions,
      });
      inflight.set(key, pending);
    }

    try {
      const adapter = await pending;
      cache.set(key, adapter);
      return adapter;
    } finally {
      inflight.delete(key);
    }
  }

  const defaultAdapter = await ensureAdapter("standard");

  const factory: ModelAwareSdkAdapterFactory = (modelClass?: ModelClass) => {
    const effectiveClass: ModelClass = modelClass ?? "standard";
    const resolved = modelResolver.resolve(effectiveClass);
    const key = cacheKey(resolved);
    const hit = cache.get(key);
    if (hit) return hit;

    // workflow-runner resolveAdapter() calls factory() synchronously for primary.
    if (effectiveClass === "standard") {
      return defaultAdapter;
    }

    return ensureAdapter(effectiveClass);
  };

  return {
    factory,
    defaultAdapter,
    modelResolver,
    resolveAdapter: ensureAdapter,
  };
}
